/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { isShipped, isSystemManaged } from './artifact-stewardship';

/**
 * **Who may see and who may change an artifact — the one projection** (`RQ11`, `#1510`).
 *
 * Six predicates answered this before, in four places, and they disagreed where an operator could reach
 * them: ad_ops saw a private system source in the list and got a 404 on its clone, clone reached sources
 * the list hid, and a strategy list showed an ownerless strategy marked private. This is the rule all of
 * them retired into. The server stamps its answer on what it reads, and the console imports this module
 * (`@artifact-access`) rather than writing a second one, so a button and the route behind it cannot
 * disagree.
 *
 * **The rule** (Todd, 2026-10-05, `RQ11b`):
 * - **see** — an admin; the owner; anyone, if public.
 * - **edit / delete / transfer** — an admin; the owner; ad_ops on a system-managed artifact **it can
 *   see**, of any type.
 * - **clone / extend** — whoever may see it.
 * - **shipped content** (`managed_by = 'bidbrain'`, `RQ14`) is never edited, deleted or transferred, by
 *   anyone: it has an upstream an import may update (`#948`), so it is copied or extended instead.
 *
 * **Visibility clamps management** (`S12`, kept by Todd 2026-10-05). Stewardship is the intent and
 * visibility the cap: a private system artifact is reachable by admins alone, which is how an operator
 * pulls one back to edit or test it, and making it public again restores ad_ops without touching
 * `managedBy`. The demand-source list showed ad_ops every system source whatever its visibility, and
 * the clone route refused the same source; the list was the one that was wrong. Group stewardship
 * (`#980`) is clamped the same way, so it is a rung on the ladder rather than a change to this shape.
 *
 * **Access is not kind** (`RQ11e`). Whether an artifact is enabled, a draft or a template decides what
 * a *list* shows and what may be *selected*; it never decides whether someone may see it. A list that
 * hides disabled sources filters for kind, after this.
 *
 * Imports only `artifact-stewardship`, which imports nothing, so it can be aliased into the console.
 */

/**
 * **The acting user**, reduced to what an access decision and a change record depend on. One reading
 * for both (`#1570`): the user an access check admits is the user the change is recorded against.
 */
export interface ArtifactActor {
  /** Empty when the request carries no identifiable user, which owns nothing. */
  userId: string;
  isAdmin: boolean;
  isAdOps: boolean;
  /** How a change record names them: the email when there is one. `system` for the platform. */
  label: string;
}

/**
 * **The platform acting for itself** — a seed, a config sync. It owns nothing and administers nothing;
 * it is named so a change record never has to leave *who* blank.
 */
export const SYSTEM_ACTOR: ArtifactActor = Object.freeze({
  userId: '',
  isAdmin: false,
  isAdOps: false,
  label: 'system',
});

/**
 * **The one reading of a request's user** (`RQ11f`). `userId`, else `id`; never the email, which a
 * controller helper used to fall back to, so one actor could own a record under two different keys.
 */
export function resolveArtifactActor(user: unknown): ArtifactActor {
  const u = (user ?? {}) as { userId?: unknown; id?: unknown; role?: unknown; email?: unknown };
  const role = u.role;
  const id = [u.userId, u.id].find((v): v is string | number => typeof v === 'string' || typeof v === 'number');
  const userId = String(id ?? '').trim();
  return {
    userId,
    isAdmin: role === 'admin' || role === 'super_admin',
    isAdOps: role === 'ad_ops',
    // The label is for a person reading a record, so the email wins here, where the identity above
    // never takes it (one actor, one key).
    label: (typeof u.email === 'string' && u.email.trim()) || userId || 'unknown',
  };
}

export type ArtifactVisibility = 'public' | 'private';

/** The fields access reads. Anything with an owner satisfies it. */
export interface AccessibleArtifact {
  ownerId?: string | number | null;
  managedBy?: string | null;
  visibility?: string | null;
}

/**
 * A record written before slice `3a` (`#1211`) carried the deprecated `isPublic` alias. A **read** shape:
 * nothing writes it, and a save drops it.
 */
type LegacyVisibility = { isPublic?: boolean };

/**
 * **What an artifact's visibility is** (`RQ11d`): stated, else the legacy `isPublic`, else private when
 * someone owns it and public when nobody does. The one resolver; three copies existed.
 */
export function resolveArtifactVisibility(
  artifact: (AccessibleArtifact & LegacyVisibility) | null | undefined,
): ArtifactVisibility {
  const stated = artifact?.visibility;
  if (stated === 'public' || stated === 'private') return stated;
  if (typeof artifact?.isPublic === 'boolean') return artifact.isPublic ? 'public' : 'private';
  return artifact?.ownerId ? 'private' : 'public';
}

/** Does this actor own it? Nobody owns an artifact with no owner, and nobody unidentified owns anything. */
export function isArtifactOwner(artifact: AccessibleArtifact | null | undefined, actor: ArtifactActor): boolean {
  const ownerId = artifact?.ownerId === null || artifact?.ownerId === undefined ? '' : String(artifact.ownerId);
  return ownerId !== '' && actor.userId !== '' && ownerId === actor.userId;
}

/** Every act the projection answers. */
export interface ArtifactAccess {
  view: boolean;
  edit: boolean;
  delete: boolean;
  transfer: boolean;
  clone: boolean;
  /** Name it as a parent. A child's own write is an edit of the child. */
  extend: boolean;
}

export type ArtifactAct = keyof ArtifactAccess;

const NONE: ArtifactAccess = Object.freeze({
  view: false,
  edit: false,
  delete: false,
  transfer: false,
  clone: false,
  extend: false,
});

/** **The projection.** A missing artifact answers no to everything. */
export function artifactAccess(
  artifact: (AccessibleArtifact & LegacyVisibility) | null | undefined,
  actor: ArtifactActor,
): ArtifactAccess {
  if (!artifact) return NONE;
  const owns = actor.isAdmin || isArtifactOwner(artifact, actor);
  const view = owns || resolveArtifactVisibility(artifact) === 'public';
  // The clamp: ad_ops' stewardship of a system artifact bites only where it can see it (`S12`).
  const manages = owns || (actor.isAdOps && isSystemManaged(artifact) && view);
  // **Shipped content is managed upstream** (`RQ14`): nobody here edits, deletes or transfers it, admins
  // included. It is copied or extended instead. Adoption (transfer from bidBrain) is not offered; its
  // shape is open (`#1591`).
  const local = manages && !isShipped(artifact);
  return { view, edit: local, delete: local, transfer: local, clone: view, extend: view };
}

/**
 * **A template's parent is nothing, or another template** (`#1599`, ruled by Todd 2026-10-07). A template
 * is a baseline: extending a live artifact would let every edit to that artifact change the template and
 * everything built from it. So a copy that **is** a template may extend only a template (a variant of a
 * template is the useful case); an instance may extend either. A relation rule, not an actor's, so it
 * sits beside {@link artifactAccess} rather than in it. It applies to new links only: a template that
 * already extends a live artifact is left as it is (Todd, 2026-10-08).
 */
export function copyMayExtend(original: { isTemplate?: boolean }, copyIsTemplate: boolean): boolean {
  return !copyIsTemplate || original.isTemplate === true;
}

/** Why {@link copyMayExtend} said no, in the operator's words. One sentence for the server and the dialog. */
export function templateExtendsOnlyATemplate(noun: string): string {
  return (
    `A template starts on its own, or from another template, so it cannot follow a live ${noun}: ` +
    `every change to the ${noun} would change the template too. Save it as independent.`
  );
}

/** May the actor see it? */
export function canSeeArtifact(
  artifact: (AccessibleArtifact & LegacyVisibility) | null | undefined,
  actor: ArtifactActor,
): boolean {
  return artifactAccess(artifact, actor).view;
}

/**
 * **May the actor manage it** — what a full-list save preserves and what a transfer requires. Shipped
 * content is managed by nobody here, so a full-list save keeps it as stored. Edit and delete read
 * {@link artifactAccess}.
 */
export function managesArtifact(
  artifact: (AccessibleArtifact & LegacyVisibility) | null | undefined,
  actor: ArtifactActor,
): boolean {
  return artifactAccess(artifact, actor).transfer;
}

/** The answers a read stamps on what it hands the console, so a button and its route agree. */
export interface ArtifactAccessStamp {
  visibility: ArtifactVisibility;
  canEdit: boolean;
  canDelete: boolean;
}

/** Stamp the actor's answers onto an artifact for the console. Derived, never authored. */
export function stampArtifactAccess<T extends AccessibleArtifact>(
  artifact: T,
  actor: ArtifactActor,
): T & ArtifactAccessStamp {
  const access = artifactAccess(artifact, actor);
  return {
    ...artifact,
    visibility: resolveArtifactVisibility(artifact),
    canEdit: access.edit,
    canDelete: access.delete,
  };
}
